import React, { useEffect, useState } from "react";
import {
  Layout,
  Card,
  DatePicker,
  Button,
  Space,
  Modal,
  Collapse,
  Table,
  Typography,
  Tag,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { Row, Col, Statistic } from "antd";

import LayoutHeader from "../../inc/LayoutHeader";
import LayoutFooter from "../../inc/LayoutFooter";

import { getAPI } from "../../../utils/common";
import {
  KITCHEN_GROUP_ISSUE_LIST,
  KITCHEN_PDF_ISSUE_REPORT,
} from "../../../constants/api";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;

function KitchenIssueReport() {
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState(null);
  const [dates, setDates] = useState([
    dayjs().startOf("month").format("YYYY-MM-DD"),
    dayjs().endOf("month").format("YYYY-MM-DD"),
  ]);

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  // =========================
  // FETCH
  // =========================
  const fetchData = () => {
    let url = KITCHEN_GROUP_ISSUE_LIST;

    if (dates.length === 2) {
      url += `?date_from=${dates[0]}&date_to=${dates[1]}`;
    }

    getAPI(url, (res) => {
      setData(res.status ? res.data : []);
      setTotals(res.status ? res.totals : []);
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  // =========================
  // PDF EXPORT
  // =========================
  const handlePrintFullReport = async () => {
    let url = KITCHEN_PDF_ISSUE_REPORT;

    if (dates.length === 2) {
      url += `?date_from=${dates[0]}&date_to=${dates[1]}`;
    }
    window.open(url, "_blank");
  };

  // =========================
  // ISSUE TABLE COLUMNS
  // =========================
  const itemColumns = [
    { title: "S.No", render: (_, __, i) => i + 1 },
    { title: "Item", dataIndex: "item_name" },
    { title: "Issued", dataIndex: "qty" },
    { title: "Consumed", dataIndex: "used_qty" },
    { title: "Remaining", dataIndex: "remaining_qty" },
    { title: "Unit", dataIndex: "unit" },
    { title: "Rate", dataIndex: "price" },
    { title: "Consumed Value", dataIndex: "used_total" },
    { title: "Remaining Value", dataIndex: "remaining_total" },
    { title: "Purpose", dataIndex: "purpose" },
  ];

  return (
    <Layout>
      <LayoutHeader />

      <Content>
        <Card
          title="Kitchen Issue / Consumption Report / Remaining Report"
          extra={
            <Space>
              <DatePicker.RangePicker
                defaultValue={[
                  dayjs().startOf("month"),
                  dayjs().endOf("month"),
                ]}
                onChange={(v) => {
                  if (v) {
                    setDates([
                      dayjs(v[0]).format("YYYY-MM-DD"),
                      dayjs(v[1]).format("YYYY-MM-DD"),
                    ]);
                  } else {
                    setDates([]);
                  }
                }}
              />

              <Button type="primary" onClick={fetchData}>
                Filter
              </Button>

              <Button type="primary" onClick={handlePrintFullReport}>
                Export PDF
              </Button>
            </Space>
          }
        >
          {totals && (
            <Card style={{ marginBottom: 12 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Total Issue Value"
                    value={totals.grand_total}
                    prefix="₹"
                  />
                </Col>

                <Col span={8}>
                  <Statistic
                    title="Consumed Value"
                    value={totals.grand_consumed_total}
                    prefix="₹"
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Col>

                <Col span={8}>
                  <Statistic
                    title="Remaining Value"
                    value={totals.grand_remaining_total}
                    prefix="₹"
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Col>
              </Row>
            </Card>
          )}
          <Collapse accordion>
            {(data || []).map((group, i) => (
              <Panel
                header={
                  <Space>
                    <Tag color="blue">{group.date}</Tag>
                    <Text strong>Total Issue Value: ₹{group.date_total}</Text> /
                    <Text strong>
                      Total Consumed Value: ₹{group.total_consumed_value}
                    </Text>{" "}
                    /
                    <Text strong>
                      Total Remaining Value: ₹{group.total_remaining_value}
                    </Text>
                  </Space>
                }
                key={i}
              >
                {(group.issues || []).map((issue) => (
                  <Card
                    key={issue.id}
                    size="small"
                    style={{ marginBottom: 15, cursor: "pointer" }}
                    onClick={() => {
                      setSelectedIssue(issue);
                      setModalVisible(true);
                    }}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Space>
                        <Tag color="green">#{issue.id}</Tag>
                        <Text>
                          <b>To:</b> {issue.hand_over_to}
                        </Text>
                        <Text>
                          <b>By:</b> {issue.issue_by}
                        </Text>
                      </Space>

                      <Text type="secondary">
                        {issue.remark || "No remark"}
                      </Text>

                      <Table
                        dataSource={issue.items || []}
                        columns={itemColumns}
                        pagination={false}
                        rowKey="id"
                        size="small"
                      />
                    </Space>
                  </Card>
                ))}
              </Panel>
            ))}
          </Collapse>
        </Card>

        {/* =========================
            MODAL
        ========================= */}
        <Modal
          open={modalVisible}
          footer={null}
          onCancel={() => setModalVisible(false)}
          width={1300}
        >
          {selectedIssue && (
            <div>
              <Title level={4}>Issue #{selectedIssue.id}</Title>

              <Space direction="vertical">
                <Text>
                  <b>Date:</b>{" "}
                  {dayjs(selectedIssue.issue_date).format("DD MMM YYYY")}
                </Text>

                <Text>
                  <b>Hand Over To:</b> {selectedIssue.hand_over_to}
                </Text>

                <Text>
                  <b>Issued By:</b> {selectedIssue.issue_by}
                </Text>

                <Text>
                  <b>Remark:</b> {selectedIssue.remark || "-"}
                </Text>
              </Space>

              <Divider />

              <Table
                dataSource={selectedIssue.items || []}
                columns={itemColumns}
                pagination={false}
                rowKey="id"
              />
            </div>
          )}
        </Modal>
      </Content>

      <LayoutFooter />
    </Layout>
  );
}

export default KitchenIssueReport;
